import { cn } from '@/lib/utils';
import { useDealTagsQuery } from '@/hooks/useDealTags';

interface DealTagsDisplayProps {
  dealId: string;
  className?: string;
  /** Compact mode uses smaller chips */
  compact?: boolean;
  /** Maximum tags to show before "+N more" */
  maxTags?: number;
}

/**
 * Read-only display of tags assigned to a deal
 * Fetches tags via React Query if not provided inline
 */
export function DealTagsDisplay({ 
  dealId, 
  className, 
  compact = false,
  maxTags = 3 
}: DealTagsDisplayProps) {
  const { data: dealTags, isLoading } = useDealTagsQuery(dealId);

  if (isLoading || !dealTags || dealTags.length === 0) {
    return null;
  }

  const visibleTags = dealTags.slice(0, maxTags);
  const remainingCount = dealTags.length - maxTags;

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {visibleTags.map((dt) => (
        <TagChip
          key={dt.id}
          name={dt.tag?.name || ''}
          color={dt.tag?.color || '#6B7280'}
          compact={compact}
        />
      ))}
      {remainingCount > 0 && (
        <span className={cn(
          'inline-flex items-center rounded-full bg-muted text-muted-foreground',
          compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
        )}>
          +{remainingCount}
        </span>
      )}
    </div>
  );
}

interface InlineDealTagsDisplayProps {
  /** Pre-fetched tags from the deal query (avoids extra query) */
  tags: Array<{
    id: string;
    tag_id: string;
    stage_tags?: {
      id: string;
      name: string;
      color: string | null;
    } | null;
  }>;
  className?: string;
  compact?: boolean;
  maxTags?: number;
}

/**
 * Inline display of tags when already fetched with the deal
 * Use this in Kanban/list views where tags are joined
 */
export function InlineDealTagsDisplay({
  tags,
  className,
  compact = false,
  maxTags = 3,
}: InlineDealTagsDisplayProps) {
  if (!tags || tags.length === 0) {
    return null;
  }

  const visibleTags = tags.slice(0, maxTags);
  const remainingCount = tags.length - maxTags;

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {visibleTags.map((dt) => (
        <TagChip
          key={dt.id}
          name={dt.stage_tags?.name || ''}
          color={dt.stage_tags?.color || '#6B7280'}
          compact={compact}
        />
      ))}
      {remainingCount > 0 && (
        <span className={cn(
          'inline-flex items-center rounded-full bg-muted text-muted-foreground',
          compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
        )}>
          +{remainingCount}
        </span>
      )}
    </div>
  );
}

interface TagChipProps {
  name: string;
  color: string;
  compact?: boolean;
}

function TagChip({ name, color, compact }: TagChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
      )}
      style={{
        backgroundColor: color,
        color: getContrastColor(color),
      }}
    >
      {name}
    </span>
  );
}

/**
 * Get contrasting text color based on background
 */
function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}
